/*  Init Drawer Component

    Usage:
    <w3-drawer></w3-drawer>

    Accepts:
    - opener: {Attribute} Element id / class that trigger open drawer (ex: .js-drawer-open)
    - closer: {Attribute} Element id / class that trigger close drawer (ex: .js-drawer-close)
    - drawer-name: {Attribute} Drawer name to add body class (ex: drawer-search)
    - disable-click-outside {Attribute} Disable click outside
    - content_handle: {String} Page handle to dynamicly fetch content from
    - js--mobile-only: {Class} Is drawer mobile only
    - title: {String} Drawer title
    - content_html: {Html} Drawer content html
*/

export default class Drawer extends HTMLElement {
    constructor() {
        super();
        this.bindEvents = this.bindEvents.bind(this);
        this.openDrawer = this.openDrawer.bind(this);
        this.closeDrawer = this.closeDrawer.bind(this);
        this.onClickOutside = this.onClickOutside.bind(this);
        this.createCustomEvents = this.createCustomEvents.bind(this);
        this.onEchap = this.onEchap.bind(this);
    }

    connectedCallback() {
        this.Drawer = {};
        this.drawerEl = this;

        this.drawerSidebarEl = this.getElement('.js-drawer-sidebar');
        this.drawerContentEl = this.getElement('.js-drawer-content');
        this.hasCustomEvents = this.drawerEl.hasAttribute('has-custom-events') || false ;
        this.disableClickOutside = this.drawerEl.hasAttribute('disable-click-outside') || false ;
        this.isLockScroll = this.drawerEl.hasAttribute('lock-scroll') || false ;
        this.drawerName = this.getAttribute('drawer-name') || 'drawer';

        this.isDrawerOpen = false;
        this.isAnyDrawerVisible = false;
        this.mobileBreakpoint = 1024;
        this.bodyTopPosition = 0;
        this.speed = parseInt(this.drawerEl.getAttribute('speed-transition')) || 200 ;
        this.isDrawerMobile = this.drawerEl.classList.contains('js-drawer--mobile');
        this.SHOPIFY_LOCALE = Shopify.locale == 'fr' ? '' : `/${Shopify.locale}`;

        this.searchInput = this.querySelector('.js-input-search');

        this.isMobile = this.checkIfMobile();

        if (this.isDrawerMobile && !this.isMobile) return;

        this.bindEvents();
    }

    openDrawer() {
        if (this.isDrawerOpen) { return; }

        // very specific case if another drawer is already open
        if (document.body.classList.contains('drawer-visible')) { 
            this.isAnyDrawerVisible = true;
        }

        if (this.drawerEl.getAttribute('closer')) {
            this.drawerBtnsClose = document.querySelectorAll(this.drawerEl.getAttribute('closer'));
        } else {
            this.drawerBtnsClose = this.drawerEl.querySelectorAll('.js-drawer-close');
        }

        if (this.drawerBtnsClose !== undefined && this.drawerBtnsClose.length > 0) {
            this.drawerBtnsClose.forEach(button => {
                button.addEventListener('click', this.closeDrawer)
            });
        }

        document.body.classList.add('drawer-visible', `${this.drawerName}-visible`);

        setTimeout(() => {
            this.drawerEl.classList.add('drawer--visible');
        }, 50);

        setTimeout(() => {
            if (this.isMobile || this.isLockScroll) {
                this.lockScroll();
            }

            if (this.hasCustomEvents) {
                this.drawerEl.dispatchEvent(this.customEventDrawerOpen);
            }

            this.isDrawerOpen = true;
            document.addEventListener('keyup', this.onEchap);
            document.addEventListener('click', this.onClickOutside);
            window.lozadObserver.observe();
        }, this.speed)
    }

    closeDrawer() {
        if (!this.isDrawerOpen) {
          return;
        }

        this.drawerEl.classList.add('drawer-transition-out');
        document.removeEventListener('keyup', this.onEchap);
        document.removeEventListener('click', this.onClickOutside);

        if (this.isMobile || this.isLockScroll) {
            this.unLockScroll();
        }

        setTimeout(() => {
            this.isDrawerOpen = false;
            this.drawerEl.classList.remove('drawer--visible', 'drawer-transition-out');
            document.body.classList.remove(`${this.drawerName}-visible`);

            if (!this.isAnyDrawerVisible) {
                document.body.classList.remove('drawer-visible');
            }

            if (this.hasCustomEvents) {
                this.drawerEl.dispatchEvent(this.customEventDrawerClose);
            }

        }, this.speed);
    }

    lockScroll() {
        this.bodyTopPosition = document.documentElement.scrollTop;
        document.body.style.top = `-${this.bodyTopPosition}px`;
        document.documentElement.classList.add('lock-scroll');
    }

    unLockScroll() {
        document.documentElement.classList.remove('lock-scroll');
        window.scrollTo({ left: 0, top: this.bodyTopPosition, behavior: 'instant' });
        document.body.style.top = `unset`;
    }

    onEchap(e) {
        if (e.keyCode === 27) {
            this.closeDrawer();
        }
    }

    onClickOutside(e) {
        if (!this.drawerSidebarEl.contains(e.target) && !this.disableClickOutside) {
            this.closeDrawer();
        }
    }

    createCustomEvents() {
        this.customEventDrawerOpen = new CustomEvent('drawer--open', {
            bubbles: true,
            cancelable: true,
            composed: false,
        });

        this.customEventDrawerClose = new CustomEvent('drawer--close', {
            bubbles: true,
            cancelable: true,
            composed: false,
        });
    }

    getElement(selector, all = false) {
        // Return the element or a default value
        return all ? this.drawerEl.querySelectorAll(selector) || [] : this.drawerEl.querySelector(selector) || null;
    }

    checkIfMobile() {
        // Check if the device is mobile
        return window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;
    }

    bindEvents() {
        if (this.drawerEl.getAttribute('opener')) {
            this.drawerBtnsOpen = document.querySelectorAll(this.drawerEl.getAttribute('opener'));
        }

        if (this.drawerBtnsOpen) {
            for (const drawerBtnOpen of this.drawerBtnsOpen) {
                drawerBtnOpen.addEventListener('click', this.openDrawer)
            }
        }

        if (this.hasCustomEvents) {
            this.createCustomEvents();
        }
    }

    disconnectedCallback() {
        if (this.drawerBtnsOpen) {
            for (const drawerBtnOpen of this.drawerBtnsOpen) {
                drawerBtnOpen.removeEventListener('click', this.openDrawer)
            }
        }
 
        if (this.drawerBtnsClose !== undefined && this.drawerBtnsClose.length > 1) {
            this.drawerBtnsClose.forEach(button => {
                button.removeEventListener('click', this.closeDrawer)
            });
        }
    }
}

if (customElements.get('w3-drawer') === undefined) {
    customElements.define('w3-drawer', Drawer);
}